import { Link } from 'react-router-dom';

function Section({ title, subtitle, children }) {
  return (
    <section className="mt-14">
      <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {title}
      </h2>
      {subtitle ? (
        <p className="mt-3 max-w-4xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">{subtitle}</p>
      ) : null}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl">
      <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {title}
      </div>
      <div className="mt-4 text-sm leading-7 text-[var(--text-secondary)]">{children}</div>
    </div>
  );
}

function BulletList({ items }) {
  return (
    <ul className="mt-3 grid gap-2 text-sm text-[var(--text-secondary)]">
      {items.map((b) => (
        <li key={b} className="leading-6">
          {b}
        </li>
      ))}
    </ul>
  );
}

export default function Offerings() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_25%,rgba(6,182,212,0.14),transparent_55%),radial-gradient(circle_at_70%_20%,rgba(37,99,235,0.18),transparent_45%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Identity Management Centre of Excellence
          </div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Service Offerings Catalog
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Comprehensive Identity Solutions for the Modern Enterprise
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Discuss an engagement
            </Link>
            <Link
              to="/capabilities"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              Explore capabilities
            </Link>
          </div>
        </div>
      </div>

      <Section
        title="Our Service Portfolio"
        subtitle="The Identity Management Centre of Excellence delivers end-to-end identity solutions through four complementary service categories designed to address every stage of your identity journey—from strategic planning through ongoing operations."
      >
        <div className="grid gap-4 lg:grid-cols-4">
          <Card title="Advisory Services">Strategic consulting, assessments, and roadmap development</Card>
          <Card title="Implementation Services">Design, build, and deploy identity solutions</Card>
          <Card title="Managed Services">Ongoing operations, monitoring, and optimization</Card>
          <Card title="Accelerators & IP">Pre-built solutions, frameworks, and tools</Card>
        </div>
      </Section>

      <Section
        title="Advisory Services"
        subtitle="Strategic consulting services that help organizations assess their current identity posture, define target state architecture, and develop actionable roadmaps for identity transformation."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Identity Maturity Assessment">
            <div className="font-semibold text-[var(--text-primary)]">Scope & Approach</div>
            <BulletList
              items={[
                "360-degree assessment covering IAM, PAM, IGA, CIAM, and machine identity domains",
                "Evaluation against industry frameworks (NIST, ISO 27001, Gartner IAM maturity model)",
                "Stakeholder interviews across IT, Security, HR, Compliance, and Business units",
                "Technical discovery of existing identity infrastructure and integrations",
                "Gap analysis against regulatory requirements (RBI, SEBI, DPDP, PCI-DSS, HIPAA)",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Deliverables</div>
            <BulletList
              items={[
                "Identity Maturity Scorecard with domain-wise ratings",
                "Gap analysis report with prioritized remediation recommendations",
                "Executive presentation with benchmark comparisons",
                "Duration: 4-6 weeks",
              ]}
            />
          </Card>

          <Card title="Identity Strategy & Roadmap">
            <div className="font-semibold text-[var(--text-primary)]">Scope & Approach</div>
            <BulletList
              items={[
                "Business requirement workshops with key stakeholders",
                "Target state architecture definition covering all identity domains",
                "Technology evaluation and vendor selection support",
                "Business case development with ROI analysis",
                "Multi-year transformation roadmap with quick wins identification",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Deliverables</div>
            <BulletList
              items={[
                "Identity Strategy Document with vision, principles, and success metrics",
                "Target State Architecture blueprints",
                "Phased Implementation Roadmap (12-36 months)",
                "Investment estimate and resource plan",
                "Duration: 6-10 weeks",
              ]}
            />
          </Card>

          <Card title="Zero Trust Architecture Assessment">
            <div className="font-semibold text-[var(--text-primary)]">Scope & Approach</div>
            <BulletList
              items={[
                "Current state assessment against NIST Zero Trust Architecture (SP 800-207)",
                "Identity-centric security posture evaluation",
                "Network segmentation and microsegmentation analysis",
                "Device trust and endpoint security assessment",
                "Data classification and protection review",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Deliverables</div>
            <BulletList
              items={[
                "Zero Trust Readiness Assessment Report",
                "Target Zero Trust Architecture Design",
                "Prioritized implementation roadmap",
                "Duration: 4-8 weeks",
              ]}
            />
          </Card>

          <Card title="Compliance & Regulatory Advisory">
            <div className="font-semibold text-[var(--text-primary)]">Coverage Areas</div>
            <BulletList
              items={[
                "Banking & Finance: RBI Cybersecurity Framework, SEBI CSCRF, PCI-DSS, SOX",
                "Government: IT Act, DPDP Act, CERT-In guidelines, MeitY standards",
                "Healthcare: HIPAA, DISHA, NABH requirements",
                "Critical Infrastructure: CEA guidelines, NCIIPC requirements, IEC 62443",
                "Global Operations: GDPR, CCPA, ISO 27001, SOC 2",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Deliverables</div>
            <BulletList
              items={["Regulatory gap analysis and compliance roadmap", "Control mapping documentation", "Audit preparation support"]}
            />
          </Card>

          <Card title="Vendor Evaluation & Selection">
            <div className="font-semibold text-[var(--text-primary)]">Scope & Approach</div>
            <BulletList
              items={[
                "Requirements definition and weighting",
                "RFP/RFI development and vendor outreach",
                "Proof of Concept design and evaluation criteria",
                "TCO analysis and negotiation support",
                "Final recommendation with implementation considerations",
                "Duration: 6-12 weeks (depending on PoC requirements)",
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section
        title="Implementation Services"
        subtitle="End-to-end design, build, and deployment services for identity solutions across all domains. Our certified architects and engineers deliver production-ready implementations following industry best practices."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Identity & Access Management (IAM)">
            <div className="font-semibold text-[var(--text-primary)]">Capabilities</div>
            <BulletList
              items={[
                "Single Sign-On (SSO): SAML, OIDC, WS-Federation integration across cloud and on-premises applications",
                "Multi-Factor Authentication (MFA): Adaptive authentication, passwordless (FIDO2/passkeys), biometrics",
                "Directory Services: Active Directory, Azure AD, LDAP consolidation and modernization",
                "Federation: B2B Partner federation, inter-organizational trust relationships",
                "Conditional Access: Risk-based access policies, device trust, location-based controls",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Supported Platforms</div>
            <BulletList
              items={[
                "Microsoft Entra ID (Azure AD), Okta, Ping Identity, ForgeRock, OneLogin",
                "Open-source: Keycloak, Gluu, Authentik",
              ]}
            />
          </Card>

          <Card title="Identity Governance & Administration (IGA)">
            <div className="font-semibold text-[var(--text-primary)]">Capabilities</div>
            <BulletList
              items={[
                "Lifecycle Management: Joiner-Mover-Leaver automation with HRMS integration",
                "Access Request & Approval: Self-service access requests with configurable approval workflows",
                "Access Certification: Periodic access reviews, manager/owner certifications, risk-based campaigns",
                "Role Management: Role mining, RBAC/ABAC design, role lifecycle management",
                "Segregation of Duties: SoD policy definition, violation detection, remediation workflows",
                "Connector Development: Custom connectors for legacy and proprietary applications",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Supported Platforms</div>
            <BulletList
              items={[
                "SailPoint IdentityNow/IIQ, Saviynt, One Identity, Microsoft Identity Governance",
                "Open-source: Apache Syncope, midPoint",
              ]}
            />
          </Card>

          <Card title="Privileged Access Management (PAM)">
            <div className="font-semibold text-[var(--text-primary)]">Capabilities</div>
            <BulletList
              items={[
                "Credential Vaulting: Secure storage of privileged credentials with automatic rotation",
                "Session Management: Session recording, real-time monitoring, keystroke logging",
                "Just-in-Time Access: Time-bound privileged access with approval workflows",
                "Secrets Management: API keys, certificates, database credentials for DevOps pipelines",
                "OT/ICS PAM: Specialized controls for SCADA, DCS, and industrial systems",
                "Database PAM: DBA access controls with query-level auditing",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Supported Platforms</div>
            <BulletList
              items={[
                "CyberArk, BeyondTrust, Delinea (Thycotic/Centrify), HashiCorp Vault",
                "Cloud-native: AWS Secrets Manager, Azure Key Vault, GCP Secret Manager",
              ]}
            />
          </Card>

          <Card title="Customer Identity & Access Management (CIAM)">
            <div className="font-semibold text-[var(--text-primary)]">Capabilities</div>
            <BulletList
              items={[
                "Registration & Onboarding: Social login, progressive profiling, self-service registration",
                "Identity Verification: eKYC, Video KYC, document verification, liveness detection",
                "Consent Management: Granular consent collection, preference centers, audit trails",
                "Authentication: Passwordless, biometric, adaptive/risk-based authentication",
                "Profile Management: Self-service profile updates, data portability, account deletion",
                "Fraud Prevention: Bot detection, account takeover prevention, device fingerprinting",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Supported Platforms</div>
            <BulletList
              items={["Auth0, Ping Identity, ForgeRock, SAP Customer Data Cloud, Okta CIC"]}
            />
          </Card>

          <Card title="Identity Threat Detection & Response (ITDR)">
            <div className="font-semibold text-[var(--text-primary)]">Capabilities</div>
            <BulletList
              items={[
                "Identity Attack Detection: Credential stuffing, password spray, MFA fatigue, token theft",
                "Behavioral Analytics: User and entity behavior analytics (UEBA) for anomaly detection",
                "AD/Entra Protection: Directory attack detection (DCSync, Golden Ticket, Kerberoasting)",
                "Automated Response: SOAR integration for automated containment and remediation",
                "Dark Web Monitoring: Credential leak detection and alerting",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Supported Platforms</div>
            <BulletList
              items={["CrowdStrike Falcon Identity, Microsoft Defender for Identity, Semperis, Silverfort"]}
            />
          </Card>

          <Card title="Cloud & Multi-Cloud Identity">
            <div className="font-semibold text-[var(--text-primary)]">Capabilities</div>
            <BulletList
              items={[
                "Cloud IAM: AWS IAM, Azure RBAC, GCP IAM policy design and implementation",
                "Cross-Cloud Federation: Unified identity across AWS, Azure, GCP with consistent policies",
                "Workload Identity: Service account management, workload identity federation",
                "Kubernetes Identity: RBAC, service mesh identity (Istio), secrets management",
                "CIEM: Cloud Infrastructure Entitlement Management for least-privilege enforcement",
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section
        title="Managed Services"
        subtitle="Ongoing operational support, monitoring, and optimization services that ensure your identity infrastructure remains secure, compliant, and aligned with evolving business needs."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Identity Operations (IDOps)">
            <div className="font-semibold text-[var(--text-primary)]">Service Scope</div>
            <BulletList
              items={[
                "L1/L2/L3 Support: Tiered support model with defined SLAs for incident resolution",
                "User Provisioning: Account creation, modification, termination processing",
                "Access Request Fulfillment: Processing access requests and managing approvals",
                "Password/MFA Support: Reset assistance, token provisioning, lockout resolution",
                "Application Onboarding: Integrating new applications into identity infrastructure",
                "Change Management: Controlled changes to identity configurations and policies",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Service Levels</div>
            <BulletList
              items={[
                "24x7, 16x5, or 8x5 coverage options",
                "Severity-based response times (P1: 15 min, P2: 1 hour, P3: 4 hours, P4: 1 business day)",
                "Monthly service reviews and continuous improvement",
              ]}
            />
          </Card>

          <Card title="Identity Security Monitoring">
            <div className="font-semibold text-[var(--text-primary)]">Service Scope</div>
            <BulletList
              items={[
                "Real-Time Monitoring: 24x7 monitoring of authentication events, privileged activities, and access patterns",
                "Threat Detection: Identity attack detection including credential stuffing, account takeover, privilege escalation",
                "Alert Triage: Investigation and escalation of security alerts",
                "Incident Response: Containment and remediation support for identity incidents",
                "Threat Intelligence: Integration of identity-specific threat feeds and IoCs",
                "SIEM Integration: Correlation with broader security monitoring (Chronicle, Splunk, Sentinel)",
              ]}
            />
          </Card>

          <Card title="Identity Governance Services">
            <div className="font-semibold text-[var(--text-primary)]">Service Scope</div>
            <BulletList
              items={[
                "Access Certification Campaigns: Design, execution, and follow-up of periodic access reviews",
                "SoD Monitoring: Continuous monitoring for segregation of duties violations",
                "Orphan Account Management: Detection and remediation of accounts without owners",
                "Privileged Account Review: Regular review and attestation of privileged access",
                "Policy Compliance: Monitoring adherence to identity policies and standards",
                "Audit Support: Evidence collection and reporting for internal/external audits",
              ]}
            />
          </Card>

          <Card title="Platform Administration & Optimization">
            <div className="font-semibold text-[var(--text-primary)]">Service Scope</div>
            <BulletList
              items={[
                "Health Monitoring: Proactive monitoring of identity platform health and availability",
                "Patch Management: Security patches and version upgrades",
                "Performance Optimization: Tuning for optimal authentication and provisioning performance",
                "Capacity Planning: Scaling recommendations based on usage trends",
                "Backup & Recovery: Regular backups and DR testing",
                "License Management: Usage tracking and license optimization",
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section
        title="Accelerators & Intellectual Property"
        subtitle="Pre-built solutions, frameworks, and tools developed by our COE that accelerate implementation timelines, reduce risk, and embed best practices into your identity programs."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Industry Accelerator Packs">
            <div className="font-semibold text-[var(--text-primary)]">BFSI Accelerator Pack</div>
            <BulletList
              items={[
                "Pre-built RBI/SEBI compliance policy templates",
                "Core banking system connectors (Finacle, Flexcube, TCS BaNCS)",
                "Maker-checker workflow templates",
                "Video KYC integration framework",
                "Branch-level role hierarchy templates",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Government Accelerator Pack</div>
            <BulletList
              items={[
                "Aadhaar/DigiLocker integration components",
                "Inter-department federation templates",
                "IT Act/DPDP compliance policy framework",
                "Employee transfer workflow automation",
                "CAG audit report templates",
              ]}
            />
            <div className="mt-5 font-semibold text-[var(--text-primary)]">Critical Infrastructure Accelerator Pack</div>
            <BulletList
              items={[
                "OT/IT identity bridge framework",
                "SCADA system PAM templates",
                "Shift-based operator access workflows",
                "CEA/NCIIPC compliance mapping",
                "Emergency access break-glass procedures",
              ]}
            />
          </Card>

          <Card title="Integration Framework">
            <div className="font-semibold text-[var(--text-primary)]">Available Connectors</div>
            <BulletList
              items={[
                "ERP Systems: SAP HCM/SuccessFactors, Oracle HCM, Workday",
                "ITSM: ServiceNow, BMC Remedy, Jira Service Management",
                "Cloud Platforms: AWS, Azure, GCP IAM integration templates",
                "SaaS Applications: Salesforce, Microsoft 365, Google Workspace, Slack",
                "Legacy Systems: Mainframe, AS/400, custom application connector framework",
                "Security Tools: SIEM (Chronicle, Splunk, Sentinel), SOAR platforms",
              ]}
            />
          </Card>

          <Card title="Assessment & Discovery Tools">
            <BulletList
              items={[
                "Identity Discovery Scanner: Automated discovery of identity sources, accounts, and entitlements",
                "Privileged Account Discovery: Scan for privileged accounts across servers, databases, network devices",
                "AD Security Assessment: Active Directory security posture evaluation",
                "Cloud IAM Analyzer: Multi-cloud permission analysis and least-privilege recommendations",
                "Compliance Checker: Automated compliance validation against regulatory frameworks",
              ]}
            />
          </Card>

          <Card title="Migration & Modernization Toolkit">
            <BulletList
              items={[
                "Directory Migration Toolkit: AD to Azure AD, LDAP consolidation, directory synchronization",
                "IGA Migration Framework: Policy and configuration migration between IGA platforms",
                "Application SSO Migration: Bulk application migration to modern authentication",
                "Password Migration Utility: Seamless credential migration with minimal user impact",
              ]}
            />
          </Card>

          <Card title="Reporting & Analytics Platform">
            <BulletList
              items={[
                "Executive Dashboard: Identity KPIs, risk metrics, and trend analysis",
                "Compliance Reports: Pre-formatted reports for RBI, SOX, PCI-DSS, ISO 27001",
                "Access Analytics: Entitlement analysis, access patterns, risk scoring",
                "Privileged Activity Reports: Privileged session analytics and anomaly detection",
                "Operational Metrics: Provisioning SLAs, authentication success rates, ticket volumes",
              ]}
            />
          </Card>
        </div>
      </Section>

      <Section title="Engagement Models" subtitle="Flexible engagement options to match your organization's needs and preferences.">
        <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03]">
          <div className="grid grid-cols-1 gap-px bg-white/10 lg:grid-cols-3">
            <div className="bg-[var(--bg-secondary)]/50 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              Model
            </div>
            <div className="bg-[var(--bg-secondary)]/50 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              Description
            </div>
            <div className="bg-[var(--bg-secondary)]/50 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              Best For
            </div>

            {[
              {
                model: "Fixed Price",
                desc: "Defined scope, timeline, and cost for specific deliverables",
                best: "Well-defined projects with clear requirements",
              },
              {
                model: "Time & Materials",
                desc: "Flexible engagement with resources billed based on effort",
                best: "Evolving requirements, discovery phases",
              },
              {
                model: "Managed Service",
                desc: "Ongoing operational responsibility with SLA-based pricing",
                best: "Continuous operations, support services",
              },
              {
                model: "Staff Augmentation",
                desc: "Skilled resources embedded in your team",
                best: "Capacity expansion, knowledge transfer",
              },
              {
                model: "Outcome-Based",
                desc: "Pricing tied to achievement of defined outcomes",
                best: "Transformation programs with measurable goals",
              },
              {
                model: "Hybrid",
                desc: "Combination of models across program phases",
                best: "Large programs with varying needs",
              },
            ].map((row) => (
              <div key={row.model} className="contents">
                <div className="bg-white/[0.02] px-5 py-4 text-sm font-semibold text-[var(--text-primary)]">
                  {row.model}
                </div>
                <div className="bg-white/[0.02] px-5 py-4 text-sm text-[var(--text-secondary)]">{row.desc}</div>
                <div className="bg-white/[0.02] px-5 py-4 text-sm text-[var(--text-secondary)]">{row.best}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section title="Why Choose Our Identity COE?">
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            "Deep Expertise: Certified professionals across all major identity platforms with 500+ successful implementations",
            "Industry Focus: Specialized practices for Government, BFSI, Infrastructure, and Digital Native organizations",
            "Accelerated Delivery: Pre-built accelerators reduce implementation time by 30-40%",
            "Compliance First: Deep understanding of Indian and global regulatory requirements",
            "Vendor Agnostic: Unbiased recommendations across commercial and open-source platforms",
            "End-to-End: From strategy through operations—a single Partner for your identity journey",
          ].map((o) => (
            <div key={o} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-[var(--text-secondary)]">
              <span className="text-[var(--text-primary)]">{o.split(":")[0]}:</span>
              {o.includes(":") ? " " + o.split(":").slice(1).join(":").trim() : ""}
            </div>
          ))}
        </div>
      </Section>

      <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Ready to engage?
            </h2>
            <p className="mt-4 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
              Contact us to discuss how our Identity Management COE can help secure and enable your organization.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[var(--gradient-card)] p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Next step</div>
            <div className="mt-3 text-xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Schedule a working session
            </div>
            <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">
              We will align your objectives, map the identity domains in scope, and recommend an engagement model and timeline.
            </p>
            <Link
              to="/contact"
              className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white"
            >
              Contact the CoE
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
